import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CaptureChartButton from './CaptureChartButton';

const { FiMaximize2, FiMinimize2, FiRefreshCw, FiBarChart2, FiTrendingUp, FiCamera, FiSend, FiCheck, FiCopy, FiExternalLink } = FiIcons;

const AdvancedTradingChart = ({ symbol, market, onSymbolChange, onChartAnalysis, currentData, isAnalyzing, onCaptureForAnalysis, onChartCaptured, capturedCount = 0 }) => {
  const containerRef = useRef();
  const widgetRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [chartReady, setChartReady] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('15');
  const [chartType, setChartType] = useState('candlestick');
  const [showCapturePanel, setShowCapturePanel] = useState(false);
  const [captureSuccess, setCaptureSuccess] = useState(false);

  // 🚀 SYMBOLES ULTRA-FIABLES TESTÉS ET VALIDÉS
  const getWorkingSymbols = () => {
    if (market === 'Gold') {
      return [
        { symbol: 'XAUUSD', name: 'Gold Spot (XAU/USD)', tradingViewSymbol: 'OANDA:XAUUSD' },
        { symbol: 'XAUEUR', name: 'Gold EUR (XAU/EUR)', tradingViewSymbol: 'OANDA:XAUEUR' }
      ];
    } else if (market === 'Indices') {
      return [
        { symbol: 'US30', name: 'Dow Jones US30', tradingViewSymbol: 'OANDA:US30USD' },
        { symbol: 'NAS100', name: 'Nasdaq 100', tradingViewSymbol: 'OANDA:NAS100USD' },
        { symbol: 'SPP500', name: 'S&P 500', tradingViewSymbol: 'OANDA:SPX500USD' },
        { symbol: 'GER30', name: 'DAX Germany 30', tradingViewSymbol: 'OANDA:DE30EUR' }
      ];
    } else if (market === 'Crypto') {
      return [
        { symbol: 'BTCUSD', name: 'Bitcoin (BTC/USD)', tradingViewSymbol: 'COINBASE:BTCUSD' },
        { symbol: 'ETHUSD', name: 'Ethereum (ETH/USD)', tradingViewSymbol: 'COINBASE:ETHUSD' },
        { symbol: 'SOLUSD', name: 'Solana (SOL/USD)', tradingViewSymbol: 'COINBASE:SOLUSD' },
        { symbol: 'XRPUSD', name: 'Ripple (XRP/USD)', tradingViewSymbol: 'BITSTAMP:XRPUSD' },
        { symbol: 'ADAUSD', name: 'Cardano (ADA/USD)', tradingViewSymbol: 'COINBASE:ADAUSD' },
        { symbol: 'DOGEUSD', name: 'Dogecoin (DOGE/USD)', tradingViewSymbol: 'COINBASE:DOGEUSD' },
        { symbol: 'AVAXUSD', name: 'Avalanche (AVAX/USD)', tradingViewSymbol: 'COINBASE:AVAXUSD' },
        { symbol: 'LINKUSD', name: 'Chainlink (LINK/USD)', tradingViewSymbol: 'COINBASE:LINKUSD' },
        { symbol: 'MATICUSD', name: 'Polygon (MATIC/USD)', tradingViewSymbol: 'COINBASE:MATICUSD' },
        { symbol: 'DOTUSD', name: 'Polkadot (DOT/USD)', tradingViewSymbol: 'COINBASE:DOTUSD' }
      ];
    }
    return [{ symbol: 'XAUUSD', name: 'Gold Spot (XAU/USD)', tradingViewSymbol: 'OANDA:XAUUSD' }];
  };

  const timeframes = [
    { value: '1', label: '1m' },
    { value: '5', label: '5m' },
    { value: '15', label: '15m' },
    { value: '30', label: '30m' },
    { value: '60', label: '1H' },
    { value: '240', label: '4H' },
    { value: 'D', label: '1D' },
    { value: 'W', label: '1W' }
  ];

  const chartTypes = [
    { value: 'candlestick', label: 'Candlestick', icon: FiBarChart2 },
    { value: 'line', label: 'Line', icon: FiTrendingUp }
  ];

  const currentSymbols = getWorkingSymbols();
  const currentSymbol = symbol || currentSymbols[0].symbol;
  const currentSymbolData = currentSymbols.find(s => s.symbol === currentSymbol) || currentSymbols[0];

  // 🚀 FONCTION DE CHARGEMENT ULTRA-FIABLE
  const loadChart = () => {
    if (!containerRef.current) return;

    console.log(`🚀 Loading RELIABLE chart: ${currentSymbolData.tradingViewSymbol}`);
    setIsLoading(true);
    setChartReady(false);

    // Nettoyer les timeouts précédents
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }

    // Nettoyer complètement l'ancien widget
    if (widgetRef.current) {
      try {
        widgetRef.current.remove();
      } catch (e) {
        console.log('Widget cleanup:', e);
      }
      widgetRef.current = null;
    }
    
    containerRef.current.innerHTML = '';

    // ID unique
    const containerId = `tv_chart_${Math.random().toString(36).substr(2, 9)}`;
    containerRef.current.id = containerId;

    // ⚡ CHARGEMENT AVEC SYMBOLES TESTÉS
    try {
      // Configuration ULTRA-SIMPLE et FIABLE
      const config = {
        autosize: true,
        symbol: currentSymbolData.tradingViewSymbol,
        interval: selectedTimeframe,
        timezone: "Etc/UTC",
        theme: "dark",
        style: chartType === 'candlestick' ? "1" : "2",
        locale: "en",
        toolbar_bg: "#1f2937",
        enable_publishing: false,
        hide_top_toolbar: false,
        hide_legend: false,
        save_image: false,
        container_id: containerId,
        studies: [], // Pas d'indicateurs pour la vitesse
        loading_screen: { backgroundColor: "#1f2937" },
        overrides: {
          "paneProperties.background": "#1f2937",
          "paneProperties.vertGridProperties.color": "#374151",
          "paneProperties.horzGridProperties.color": "#374151"
        }
      };

      console.log(`⚡ Creating RELIABLE TradingView widget with ${currentSymbolData.tradingViewSymbol}`);
      
      // Créer le widget avec symboles testés
      widgetRef.current = new window.TradingView.widget(config);

      // ⚡ TIMEOUT RAPIDE - 2 secondes max
      loadTimeoutRef.current = setTimeout(() => {
        console.log('✅ Chart loading complete (timeout)');
        setChartReady(true);
        setIsLoading(false);
      }, 2000); // Ultra-rapide

      // ⚡ DÉTECTION INSTANTANÉE DU CHARGEMENT
      const checkInterval = setInterval(() => {
        const iframe = containerRef.current?.querySelector('iframe');
        if (iframe) {
          console.log('✅ Chart iframe detected - READY!');
          setChartReady(true);
          setIsLoading(false);
          clearInterval(checkInterval);
          if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
          }
        }
      }, 300); // Check toutes les 300ms

      // Nettoyer l'interval après 3 secondes max
      setTimeout(() => clearInterval(checkInterval), 3000);

    } catch (error) {
      console.error('❌ Chart creation error:', error);
      setIsLoading(false);
      
      // Retry ultra-rapide avec fallback
      setTimeout(() => {
        console.log('🔄 Ultra-fast retry...');
        loadChart();
      }, 500); // Retry après 500ms seulement
    }
  };

  // Charger le chart quand les paramètres changent
  useEffect(() => {
    if (!window.TradingView) {
      console.log('📥 Loading TradingView script...');
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        console.log('✅ TradingView script loaded');
        // ⚡ CHARGEMENT IMMÉDIAT
        setTimeout(() => loadChart(), 100);
      };
      script.onerror = () => {
        console.error('❌ Failed to load TradingView script');
        setIsLoading(false);
      };
      document.head.appendChild(script);
    } else {
      loadChart();
    }

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      if (widgetRef.current) {
        try {
          widgetRef.current.remove();
        } catch (e) {}
        widgetRef.current = null;
      }
    };
  }, [currentSymbol, market, selectedTimeframe, chartType]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const refreshChart = () => {
    console.log('🔄 Manual refresh with RELIABLE symbols');
    loadChart();
  };

  // 📸 TOGGLE INSTRUCTIONS PANEL - Just show/hide the capture instructions
  const handleCaptureForAnalysis = () => {
    setShowCapturePanel(prev => !prev);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-purple-500/30 overflow-hidden shadow-lg shadow-purple-500/10 ${
        isFullscreen ? 'fixed inset-4 z-50' : 'relative h-full'
      }`}
    >
      {/* Header - 2026 Clean Design */}
      <div className="border-b border-purple-500/20 bg-gray-900/60 backdrop-blur-sm">
        {/* Top Row - Symbol & Actions */}
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left - Symbol Info */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center border border-purple-500/30 shrink-0">
              <SafeIcon icon={FiBarChart2} className="w-4 h-4 text-purple-400" />
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-white truncate">
                  {currentSymbolData.name}
                </h3>
                <span className="text-[10px] text-cyan-400 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 px-2 py-0.5 rounded-full font-mono shrink-0">
                  {currentSymbolData.tradingViewSymbol}
                </span>
              </div>
            </div>
            
            {/* Symbol dropdown removed — single source of truth is the
                asset selector section above the chart card in TradingInterface. */}
          </div>

          {/* Right - Action Buttons */}
          <div className="flex items-center gap-1.5 ml-3 shrink-0">
            <button
              onClick={refreshChart}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all"
              title="Refresh Chart"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Bottom Row - Controls */}
        <div className="flex items-center gap-2 px-4 pb-3 flex-wrap">
          {/* Timeframes */}
          <div className="flex items-center bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl p-1 border border-purple-500/30">
            {timeframes.map((tf) => (
              <button
                key={tf.value}
                onClick={() => setSelectedTimeframe(tf.value)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-all ${
                  selectedTimeframe === tf.value
                    ? 'bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 text-cyan-400 shadow-lg shadow-purple-500/25'
                    : 'text-gray-400 hover:text-cyan-400 hover:bg-white/10'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>

          {/* Chart Type */}
          <div className="flex items-center bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl p-1 border border-purple-500/30">
            {chartTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setChartType(type.value)}
                className={`p-2 rounded-xl transition-all ${
                  chartType === type.value
                    ? 'bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 text-cyan-400 shadow-lg shadow-purple-500/20'
                    : 'text-gray-400 hover:text-cyan-400 hover:bg-white/10'
                }`}
                title={type.label}
              >
                <SafeIcon icon={type.icon} className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* 📸 CAPTURE CHART — Screen Capture API + crop, replaces TradingView+Instructions buttons */}
          <CaptureChartButton
            variant="dashboard"
            targetRef={containerRef}
            currentSymbol={currentSymbolData?.tradingViewSymbol || symbol}
            currentTimeframe={timeframes.find(tf => tf.value === selectedTimeframe)?.label || '15m'}
            disabled={isAnalyzing}
            autoAnalyze={false}
            capturedCount={capturedCount}
            onCaptured={(img) => { if (typeof onChartCaptured === 'function') onChartCaptured(img); }}
          />
        </div>
      </div>

      {/* 📸 CAPTURE OVERLAY - Shows exactly what to capture */}
      <AnimatePresence>
        {showCapturePanel && (
          <>
            {/* Overlay highlighting the chart area */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 pointer-events-none"
            >
            {/* Pulsing border around chart area */}
              <div className="absolute top-[100px] left-0 right-0 bottom-0 border-4 border-dashed border-green-500 rounded-xl animate-pulse">
                <div className="absolute inset-0 bg-green-500/10"></div>
                {/* Arrow pointing to chart */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                  <div className="bg-green-600/90 text-white px-4 py-2 rounded-xl text-lg font-bold shadow-lg">
                    👇 CAPTURE THIS AREA 👇
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Instructions panel */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="absolute top-2 left-1/2 -translate-x-1/2 z-30"
            >
              <div className="bg-gray-900/98 backdrop-blur-xl border border-green-500/50 rounded-2xl p-4 shadow-2xl shadow-green-500/20 min-w-[380px] max-w-[450px]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center animate-pulse">
                    <SafeIcon icon={FiCamera} className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-green-400 font-bold text-sm">
                      📸 Step 1: Take a screenshot
                    </h4>
                    <p className="text-gray-400 text-xs">
                      Of the green area below (the TradingView chart)
                    </p>
                  </div>
                </div>
                
                {/* Visual guide */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-green-400 text-2xl">⬇️</span>
                    <p className="text-green-300 text-sm font-semibold">
                      Capture the GREEN area (chart with candles)
                    </p>
                  </div>
                  <p className="text-gray-400 text-xs">
                    Not this instruction panel, but the chart below!
                  </p>
                </div>
                
                {/* Keyboard shortcuts */}
                <div className="bg-gray-800/60 rounded-xl p-3 border border-white/5 mb-3">
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-purple-500/20 text-purple-300 px-3 py-1.5 rounded-lg border border-purple-500/30 font-mono">
                      Mac: ⌘⇧4
                    </span>
                    <span className="bg-cyan-500/20 text-cyan-300 px-3 py-1.5 rounded-lg border border-cyan-500/30 font-mono">
                      Win: Win+Shift+S
                    </span>
                  </div>
                </div>
                
                {/* Step 2 */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 mb-3">
                  <p className="text-purple-300 text-sm font-semibold mb-1">
                    📋 Step 2: Paste in the Analysis panel
                  </p>
                  <p className="text-gray-400 text-xs">
                    Scroll down → Click in the area → <strong>Ctrl+V</strong>
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowCapturePanel(false);
                      // Scroll to analysis section
                      setTimeout(() => {
                        const analysisSection = document.querySelector('[data-analysis-section]');
                        if (analysisSection) {
                          analysisSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 300);
                    }}
                    className="flex-1 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl text-sm font-medium transition-all"
                  >
                    ✅ Captured, go to panel
                  </button>
                  <button
                    onClick={() => setShowCapturePanel(false)}
                    className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm font-medium transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Chart Container */}
      <div className="relative" style={{ height: isFullscreen ? 'calc(100% - 140px)' : 'calc(100% - 100px)' }}>
        {isLoading && (
          <div className="absolute inset-0 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="text-center">
              <div className="w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full mx-auto mb-4 animate-spin"></div>
              <p className="text-white font-medium text-sm">
                Loading {currentSymbolData.name}...
              </p>
              <p className="text-gray-500 text-xs mt-1.5">
                {currentSymbolData.tradingViewSymbol}
              </p>
            </div>
          </div>
        )}

        <div 
          ref={containerRef}
          className="w-full h-full bg-gray-900"
          style={{ minHeight: '400px' }}
        />
      </div>

      {/* Footer - 2026 Clean */}
      <div className="px-4 py-2 bg-gray-900/60 border-t border-white/5 text-[11px] text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-purple-400 font-medium">TradingView</span>
          <span className="text-gray-600">•</span>
          <span>{market}</span>
          <span className="text-gray-600">•</span>
          <span className="text-cyan-400/80">{timeframes.find(tf => tf.value === selectedTimeframe)?.label}</span>
        </div>
        
        <div className="flex items-center gap-2">
          {chartReady ? (
            <>
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
              <span className="text-emerald-400">Live</span>
            </>
          ) : isLoading ? (
            <>
              <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse"></span>
              <span className="text-purple-400">Loading</span>
            </>
          ) : (
            <>
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
              <span className="text-red-400">Error</span>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdvancedTradingChart;